import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Bookmark, Check, Copy, Download, RotateCcw, Share2, Sparkles, Ticket } from "lucide-react";
import api from "@/lib/api";
import { Seo } from "@/components/Seo";
import { useAuth } from "@/context/AuthContext";
import { useAuthModal } from "@/context/AuthModalContext";
import { QUESTIONS, ROLES } from "@/data/quizData";
import { quizResult, findShow, currentPerformers } from "@/lib/quiz";
import { makeShareCard } from "@/lib/shareCard";

const SHOW_COUNT = new Set(ROLES.map((r) => r.show)).size;
const LETTERS = ["A", "B", "C", "D"];
const FILE_NAME = "my-west-end-role.png";
const CODE_PATTERN = /^[0-3]{12}$/;

function Frame({ children }) {
  return (
    <div className="relative overflow-hidden">
      <div
        className="pointer-events-none absolute inset-x-0 top-0 h-[520px]"
        aria-hidden="true"
        style={{ background: "radial-gradient(60% 70% at 50% 0%, rgba(200,155,60,0.16), transparent 70%)" }}
      />
      <div className="relative mx-auto max-w-2xl px-4 py-10 sm:px-6 sm:py-16">{children}</div>
    </div>
  );
}

function Intro({ onStart }) {
  return (
    <div className="fade-up rounded-3xl border border-[#C89B3C]/30 bg-gradient-to-br from-[#C89B3C]/15 to-[#161118]/80 p-8 text-center backdrop-blur sm:p-12">
      <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-[#C89B3C]/20 text-[#C89B3C]">
        <Sparkles className="h-7 w-7" />
      </div>
      <h1 className="mt-6 font-serif text-4xl font-semibold leading-tight text-amber-50 sm:text-5xl">
        Which West End role are you?
      </h1>
      <p className="mx-auto mt-4 max-w-md text-lg leading-8 text-amber-50/80">
        Answer {QUESTIONS.length} quick questions and we will match you with one of {ROLES.length} roles from{" "}
        {SHOW_COUNT} shows. Heroes, villains and scene-stealers all included.
      </p>
      <p className="mt-3 text-sm text-amber-100/60">About two minutes. No sign-in needed.</p>
      <button
        type="button"
        onClick={onStart}
        className="group mt-8 inline-flex items-center gap-2 rounded-full bg-[#C89B3C] px-8 py-3.5 text-lg font-semibold text-black transition-transform hover:scale-105"
      >
        Start the quiz
        <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
      </button>
    </div>
  );
}

function Question({ index, previous, picked, onAnswer, onContinue, onBack }) {
  const q = QUESTIONS[index];
  const pct = Math.round((index / QUESTIONS.length) * 100);
  const hasPrevious = previous !== null && picked === null;
  return (
    <div key={index} className="fade-up">
      <div className="flex items-center justify-between text-sm text-amber-100/70">
        <button
          type="button"
          onClick={onBack}
          className="inline-flex items-center gap-1.5 transition-colors hover:text-amber-50"
        >
          <ArrowLeft className="h-4 w-4" /> Back
        </button>
        <span>
          Question {index + 1} of {QUESTIONS.length}
        </span>
      </div>
      <div className="mt-3 h-1.5 overflow-hidden rounded-full bg-white/10" role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100}>
        <div className="h-full rounded-full bg-[#C89B3C] transition-all duration-500" style={{ width: `${pct + 100 / QUESTIONS.length}%` }} />
      </div>

      <h2 className="mt-8 font-serif text-2xl font-semibold leading-snug text-amber-50 sm:text-3xl">{q.text}</h2>

      <div className="mt-6 grid gap-3">
        {q.answers.map((a, i) => {
          const selected = picked === i || (picked === null && previous === i);
          return (
            <button
              key={i}
              type="button"
              aria-pressed={selected}
              onClick={() => onAnswer(i)}
              className={`group flex w-full items-start gap-4 rounded-2xl border p-4 text-left transition-colors ${
                selected
                  ? "border-[#C89B3C] bg-[#C89B3C]/15"
                  : "border-white/10 bg-[#161118]/70 hover:border-[#C89B3C]/60 hover:bg-[#C89B3C]/5"
              }`}
            >
              <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-[#C89B3C]/15 text-sm font-semibold text-[#C89B3C]">
                {LETTERS[i]}
              </span>
              <span className="pt-0.5 leading-7 text-amber-50/90">{a.text}</span>
            </button>
          );
        })}
      </div>

      {hasPrevious && (
        <button
          type="button"
          onClick={onContinue}
          className="group mt-6 flex w-full items-center justify-center gap-2 rounded-full bg-[#C89B3C] px-6 py-3.5 text-lg font-semibold text-black transition-transform hover:scale-[1.02]"
        >
          Continue
          <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
        </button>
      )}
    </div>
  );
}

function RunnerUp({ role, shows }) {
  const show = findShow(shows, role);
  const inner = (
    <>
      <p className="font-serif text-lg font-semibold text-amber-50">{role.role}</p>
      <p className="mt-0.5 text-sm text-amber-100/70">{role.show}</p>
    </>
  );
  const cls =
    "block rounded-2xl border border-white/10 bg-[#161118]/70 p-4 transition-colors hover:border-[#C89B3C]/60";
  return show ? (
    <Link to={`/shows/${show.slug || show.id}`} className={cls}>
      {inner}
    </Link>
  ) : (
    <div className={cls}>{inner}</div>
  );
}

function Result({ code, onRetake }) {
  const { user } = useAuth();
  const { openAuthModal } = useAuthModal();
  const choices = useMemo(() => code.split("").map(Number), [code]);
  const { winner, runnersUp } = useMemo(() => quizResult(choices), [choices]);

  const [shows, setShows] = useState(null);
  const [card, setCard] = useState(null); // { file, url }
  const [saved, setSaved] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api
      .get("/shows")
      .then((r) => setShows(r.data || []))
      .catch(() => setShows([]));
  }, []);

  // The share image is made as soon as the result appears, because phones only allow
  // sharing straight after a tap, with no waiting in between.
  useEffect(() => {
    let alive = true;
    let url = null;
    setCard(null);
    makeShareCard({ role: winner.role, show: winner.show, line: winner.line })
      .then((blob) => {
        if (!alive) return;
        url = URL.createObjectURL(blob);
        setCard({ file: new File([blob], FILE_NAME, { type: "image/png" }), url });
      })
      .catch(() => {});
    return () => {
      alive = false;
      if (url) URL.revokeObjectURL(url);
    };
  }, [winner]);

  const show = shows ? findShow(shows, winner) : null;
  const players = show ? currentPerformers(show, winner) : [];
  const showPath = show ? `/shows/${show.slug || show.id}` : null;
  const canCompare = show && (show.price_guide || (show.ticket_links || []).length > 1);

  const canShareFile = Boolean(
    card &&
      typeof navigator !== "undefined" &&
      typeof navigator.share === "function" &&
      typeof navigator.canShare === "function" &&
      navigator.canShare({ files: [card.file] })
  );

  const saveImage = () => {
    if (!card) return;
    const a = document.createElement("a");
    a.href = card.url;
    a.download = FILE_NAME;
    document.body.appendChild(a);
    a.click();
    a.remove();
    toast.success("Image saved. Post it to Instagram, TikTok or Snapchat.");
  };

  const shareImage = async () => {
    if (!card) return;
    try {
      await navigator.share({ files: [card.file] });
    } catch (err) {
      if (err && (err.name === "AbortError" || err.name === "InvalidStateError")) return;
      saveImage();
    }
  };

  const copyCaption = async () => {
    const text = `I got ${winner.role} from ${winner.show} 🎭 Which West End role are you? mytheatre.uk/quiz #westend #theatretok`;
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Caption copied. Paste it into your post.");
    } catch {
      toast.error("Could not copy that. You can type the caption yourself.");
    }
  };

  const doSave = async () => {
    setSaving(true);
    try {
      await api.post("/quiz/save", {
        role: winner.role,
        show: show ? show.name : winner.show,
        slug: show ? show.slug || "" : "",
        line: winner.line,
        code,
      });
      setSaved(true);
      toast.success("Saved to your watchlist");
    } catch {
      toast.error("Could not save that. Please try again.");
    } finally {
      setSaving(false);
    }
  };
  const save = () =>
    user ? doSave() : openAuthModal(doSave, "Sign in or create an account to save your West End role to your watchlist.");

  return (
    <div className="fade-up">
      <div className="rounded-3xl border border-[#C89B3C]/30 bg-gradient-to-br from-[#C89B3C]/15 to-[#161118]/80 p-8 backdrop-blur sm:p-10">
        <p className="text-sm text-[#C89B3C]">Your West End role is</p>
        <h1 className="mt-2 font-serif text-4xl font-semibold leading-tight text-amber-50 sm:text-6xl">{winner.role}</h1>
        <p className="mt-2 text-lg text-amber-100/80">
          from{" "}
          {showPath ? (
            <Link to={showPath} className="text-[#C89B3C] hover:underline">
              {winner.show}
            </Link>
          ) : (
            <span className="text-[#C89B3C]">{winner.show}</span>
          )}
        </p>

        <blockquote className="mt-6 border-l-4 border-[#C89B3C] pl-5 font-serif text-xl italic leading-8 text-amber-50/90">
          {winner.line}
        </blockquote>

        {players.length > 0 && (
          <p className="mt-6 text-amber-50/80">
            On stage now: <span className="font-medium text-amber-50">{players.join(" or ")}</span>
          </p>
        )}

        <div className="mt-8 flex flex-wrap gap-3">
          {showPath && (
            <Link
              to={showPath}
              className="inline-flex items-center gap-2 rounded-full bg-[#C89B3C] px-6 py-3 font-semibold text-black transition-transform hover:scale-105"
            >
              See the show <ArrowRight className="h-4 w-4" />
            </Link>
          )}
          {canCompare && (
            <Link
              to={`${showPath}/prices`}
              className="inline-flex items-center gap-2 rounded-full border border-[#C89B3C] px-6 py-3 font-semibold text-[#C89B3C] transition-colors hover:bg-[#C89B3C]/15"
            >
              <Ticket className="h-4 w-4" /> Compare ticket prices
            </Link>
          )}
          <button
            type="button"
            onClick={save}
            disabled={saved || saving}
            className="inline-flex items-center gap-2 rounded-full border border-white/20 px-6 py-3 font-semibold text-amber-50 transition-colors hover:bg-white/10 disabled:opacity-80"
          >
            {saved ? <Check className="h-4 w-4 text-[#C89B3C]" /> : <Bookmark className="h-4 w-4" />}
            {saved ? "Saved" : "Save result"}
          </button>
        </div>

        {saved && (
          <p className="mt-4 text-sm text-amber-100/70">
            It is on your watchlist now.{" "}
            <Link to="/watchlist" className="text-[#C89B3C] hover:underline">
              View watchlist
            </Link>
          </p>
        )}
      </div>

      <section className="mt-10 rounded-3xl border border-white/10 bg-[#161118]/70 p-6 sm:p-8">
        <h2 className="font-serif text-2xl font-semibold text-amber-50">Share your result</h2>
        <div className="mt-5 flex flex-col items-center gap-6 sm:flex-row sm:items-start">
          <div className="w-40 flex-shrink-0">
            {card ? (
              <img
                src={card.url}
                alt="Your share card"
                className="w-full rounded-xl border border-[#C89B3C]/40 shadow-lg"
              />
            ) : (
              <div className="aspect-[9/16] w-full animate-pulse rounded-xl bg-white/5" />
            )}
          </div>
          <div className="w-full">
            <div className="flex flex-wrap gap-3">
              {canShareFile && (
                <button
                  type="button"
                  onClick={shareImage}
                  className="inline-flex items-center gap-2 rounded-full bg-[#C89B3C] px-6 py-3 font-semibold text-black transition-transform hover:scale-105"
                >
                  <Share2 className="h-4 w-4" /> Share my result
                </button>
              )}
              <button
                type="button"
                onClick={saveImage}
                disabled={!card}
                className="inline-flex items-center gap-2 rounded-full border border-white/20 px-6 py-3 font-semibold text-amber-50 transition-colors hover:bg-white/10 disabled:opacity-60"
              >
                <Download className="h-4 w-4" /> Save image
              </button>
              <button
                type="button"
                onClick={copyCaption}
                className="inline-flex items-center gap-2 rounded-full border border-white/20 px-6 py-3 font-semibold text-amber-50 transition-colors hover:bg-white/10"
              >
                <Copy className="h-4 w-4" /> Copy caption
              </button>
            </div>
            <p className="mt-4 text-sm leading-6 text-amber-100/70">
              {canShareFile
                ? "Share my result opens your phone's share menu. Instagram, TikTok and Snapchat show up there if you have them installed. If one is missing, save the image and post it from the app."
                : "On a computer, save the image and post it from your phone. Open this page on your phone and you can share straight to Instagram, TikTok or Snapchat."}
            </p>
          </div>
        </div>
      </section>

      {runnersUp.length > 0 && (
        <div className="mt-10">
          <h2 className="font-serif text-2xl font-semibold text-amber-50">You could also be</h2>
          <div className="mt-4 grid gap-3 sm:grid-cols-2">
            {runnersUp.map((r) => (
              <RunnerUp key={r.role + r.show} role={r} shows={shows} />
            ))}
          </div>
        </div>
      )}

      <div className="mt-10 text-center">
        <button
          type="button"
          onClick={onRetake}
          className="inline-flex items-center gap-2 text-sm text-amber-100/70 transition-colors hover:text-amber-50"
        >
          <RotateCcw className="h-4 w-4" /> Take the quiz
        </button>
      </div>
    </div>
  );
}

export default function Quiz() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const urlCode = params.get("a") || "";
  const hasResult = CODE_PATTERN.test(urlCode);

  const [step, setStep] = useState(-1); // -1 is the start screen, 0 to 11 are the questions
  const [choices, setChoices] = useState([]);
  const [picked, setPicked] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [step, urlCode]);

  // The answers go into the address (for example /quiz?a=012301230123), so the result
  // is still there when someone comes back from a show page with the Back button.
  const advance = (q, all) => {
    if (q + 1 >= QUESTIONS.length) navigate(`/quiz?a=${all.join("")}`);
    else setStep(q + 1);
  };

  const answer = (a) => {
    if (picked !== null) return;
    const q = step;
    setPicked(a);
    setTimeout(() => {
      const all = [...choices];
      all[q] = a;
      setChoices(all);
      setPicked(null);
      advance(q, all);
    }, 260);
  };

  const start = () => {
    setChoices([]);
    setStep(0);
    if (hasResult) navigate("/quiz");
  };

  return (
    <>
      <Seo
        title="Which West End role are you? Take the quiz"
        description={`Answer ${QUESTIONS.length} quick questions and find out which West End character you are, from Elphaba to Fagin. Free, with no sign-in.`}
        path="/quiz"
      />
      <Frame>
        {hasResult ? (
          <Result code={urlCode} onRetake={start} />
        ) : step >= 0 && step < QUESTIONS.length ? (
          <Question
            index={step}
            previous={choices[step] ?? null}
            picked={picked}
            onAnswer={answer}
            onContinue={() => advance(step, choices)}
            onBack={() => setStep((s) => (s === 0 ? -1 : s - 1))}
          />
        ) : (
          <Intro onStart={start} />
        )}
      </Frame>
    </>
  );
}